import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import TenantLayout from '../components/TenantLayout';
import { useParams } from 'react-router-dom';

export default function CompanySettings() {
    const { user, api, checkAuth } = useAuth();
    const { theme } = useTheme();
    const { t } = useLanguage();
    const { clientSlug } = useParams();
    const [loading, setLoading] = useState(false);
    const [settings, setSettings] = useState({
        business_name: '',
        gstin: '',
        address: '',
        pan_number: '',
        bank_name: '',
        account_number: '',
        ifsc_code: '',
        branch: '',
        signature_url: '',
        logo_url: '',
        default_fy: ''
    });

    useEffect(() => {
        if (user) {
            setSettings({
                business_name: user.business_name || '',
                gstin: user.gstin || '',
                address: user.address || '',
                pan_number: user.pan_number || '',
                bank_name: user.bank_name || '',
                account_number: user.account_number || '',
                ifsc_code: user.ifsc_code || '',
                branch: user.branch || '',
                signature_url: user.signature_url || '',
                logo_url: user.logo_url || '',
                default_fy: user.default_fy || ''
            });
        }
    }, [user]);

    // FY Options
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();
    const baseYear = 2023;
    const maxYear = currentMonth >= 3 ? currentYear : currentYear - 1;
    const fyOptions = [];
    for (let y = baseYear; y <= maxYear + 1; y++) {
        fyOptions.push(`FY ${y}-${y + 1}`);
    }

    const handleSignatureUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        if (file.size > 2 * 1024 * 1024) {
            toast.error(t('Signature file too large. Max 2MB allowed.'));
            return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
            setSettings({ ...settings, signature_url: reader.result });
        };
        reader.readAsDataURL(file);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.put(`/api/tenant/${clientSlug}/settings`, settings);
            await checkAuth(); // Refresh auth context with new details
            toast.success(t('Settings updated successfully'));
        } catch (err) {
            toast.error(err.response?.data?.detail || t('Failed to update settings'));
        } finally {
            setLoading(false);
        }
    };

    const inputClass = `w-full h-12 sm:h-10 px-3 rounded-md border text-sm focus:ring-2 focus:outline-none transition-colors ${
        theme === 'dark' 
        ? 'bg-slate-900 border-slate-700 text-slate-100 focus:ring-amber-500/50' 
        : 'bg-white border-slate-300 text-slate-900 focus:ring-[#0A192F]/20'
    }`;
    const labelClass = `block text-xs font-semibold mb-1 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`;

    return (
        <TenantLayout title={t('Company Settings')} tagline={t('Manage your business profile and bank details')}>
            <div className="max-w-4xl">
                <form onSubmit={handleSave} className="space-y-8">
                    {/* General Details */}
                    <div className={`p-6 rounded-xl border ${theme === 'dark' ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'} shadow-sm`}>
                        <h2 className={`text-lg font-bold mb-4 font-['Cabinet_Grotesk'] ${theme === 'dark' ? 'text-slate-200' : 'text-[#0F284E]'}`}>{t('General Details')}</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className={labelClass}>{t('Business Name')}</label>
                                <input type="text" required value={settings.business_name} onChange={e => setSettings({...settings, business_name: e.target.value})} className={inputClass} data-testid="settings-business-name" />
                            </div>
                            <div>
                                <label className={labelClass}>{t('GSTIN')}</label>
                                <input type="text" value={settings.gstin} onChange={e => setSettings({...settings, gstin: e.target.value})} className={inputClass} data-testid="settings-gstin" />
                            </div>
                            <div className="md:col-span-2">
                                <label className={labelClass}>{t('Address')}</label>
                                <textarea value={settings.address} onChange={e => setSettings({...settings, address: e.target.value})} className={`${inputClass} py-2 h-20 resize-none`} data-testid="settings-address" />
                            </div>
                            <div>
                                <label className={labelClass}>{t('PAN Number')}</label>
                                <input type="text" value={settings.pan_number} onChange={e => setSettings({...settings, pan_number: e.target.value})} className={inputClass} data-testid="settings-pan-number" />
                            </div>
                            <div>
                                <label className={labelClass}>{t('Financial Year')}</label>
                                <select 
                                    value={settings.default_fy} 
                                    onChange={e => setSettings({...settings, default_fy: e.target.value})} 
                                    className={inputClass}
                                    data-testid="settings-default-fy"
                                >
                                    <option value="">{t('Select Financial Year')}</option>
                                    {fyOptions.map(option => (
                                        <option key={option} value={option}>{option}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Bank Details */}
                    <div className={`p-6 rounded-xl border ${theme === 'dark' ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'} shadow-sm`}>
                        <h2 className={`text-lg font-bold mb-4 font-['Cabinet_Grotesk'] ${theme === 'dark' ? 'text-slate-200' : 'text-[#0F284E]'}`}>{t('Bank Details')}</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className={labelClass}>{t('Bank Name')}</label>
                                <input type="text" value={settings.bank_name} onChange={e => setSettings({...settings, bank_name: e.target.value})} className={inputClass} data-testid="settings-bank-name" />
                            </div>
                            <div>
                                <label className={labelClass}>{t('Account Number')}</label>
                                <input type="text" value={settings.account_number} onChange={e => setSettings({...settings, account_number: e.target.value})} className={inputClass} data-testid="settings-account-number" />
                            </div>
                            <div>
                                <label className={labelClass}>{t('IFSC Code')}</label>
                                <input type="text" value={settings.ifsc_code} onChange={e => setSettings({...settings, ifsc_code: e.target.value})} className={inputClass} data-testid="settings-ifsc-code" />
                            </div>
                            <div>
                                <label className={labelClass}>{t('Branch')}</label>
                                <input type="text" value={settings.branch} onChange={e => setSettings({...settings, branch: e.target.value})} className={inputClass} data-testid="settings-branch" />
                            </div>
                        </div>
                    </div>

                    {/* Signature */}
                    <div className={`p-6 rounded-xl border ${theme === 'dark' ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'} shadow-sm`}>
                        <h2 className={`text-lg font-bold mb-4 font-['Cabinet_Grotesk'] ${theme === 'dark' ? 'text-slate-200' : 'text-[#0F284E]'}`}>{t('Authorized Signature')}</h2>
                        <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                            <div className={`w-full sm:w-48 h-32 sm:h-24 border-2 border-dashed rounded-lg flex items-center justify-center overflow-hidden ${theme === 'dark' ? 'border-slate-600 bg-slate-900/50' : 'border-slate-300 bg-slate-50'}`}>
                                {settings.signature_url ? (
                                    <img src={settings.signature_url} alt="Signature" className="max-w-full max-h-full object-contain p-2" data-testid="settings-signature-preview" />
                                ) : (
                                    <span className={`text-sm ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`}>{t('No Signature')}</span>
                                )}
                            </div>
                            <div className="w-full sm:w-auto text-center sm:text-left">
                                <input 
                                    type="file" 
                                    accept="image/*" 
                                    onChange={handleSignatureUpload}
                                    id="signature-upload"
                                    className="hidden"
                                    data-testid="settings-signature-upload"
                                />
                                <label 
                                    htmlFor="signature-upload"
                                    className={`cursor-pointer inline-flex items-center justify-center w-full sm:w-auto px-4 py-3 sm:py-2 rounded-lg text-sm font-medium transition-colors ${
                                        theme === 'dark'
                                        ? 'bg-slate-700 hover:bg-slate-600 text-slate-200'
                                        : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                                    }`}
                                >
                                    {t('Upload Signature')}
                                </label>
                                <p className={`text-xs mt-2 ${theme === 'dark' ? 'text-slate-500' : 'text-slate-500'}`}>{t('Max 2MB. Transparent PNG recommended.')}</p>
                            </div>
                        </div>
                    </div>
                    
                    {/* Logo Upload */}
                    <div className={`p-6 rounded-xl border ${theme === 'dark' ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'} shadow-sm`}>
                        <h2 className={`text-lg font-bold mb-4 font-['Cabinet_Grotesk'] ${theme === 'dark' ? 'text-slate-200' : 'text-[#0F284E]'}`}>{t('Company Logo')}</h2>
                        <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                            <div className={`w-32 h-32 border-2 border-dashed rounded-lg flex items-center justify-center overflow-hidden ${theme === 'dark' ? 'border-slate-600 bg-slate-900/50' : 'border-slate-300 bg-slate-50'}`}>
                                {settings.logo_url ? (
                                    <img src={settings.logo_url} alt="Logo" className="max-w-full max-h-full object-contain p-2" data-testid="settings-logo-preview" />
                                ) : (
                                    <span className={`text-sm ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`}>{t('No Logo')}</span>
                                )}
                            </div>
                            <div className="w-full sm:w-auto text-center sm:text-left">
                                <input 
                                    type="file" 
                                    accept="image/*" 
                                    onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (!file) return;
                                        if (file.size > 2 * 1024 * 1024) {
                                            toast.error(t('Logo file too large. Max 2MB allowed.'));
                                            return;
                                        }
                                        const reader = new FileReader();
                                        reader.onloadend = () => {
                                            setSettings({ ...settings, logo_url: reader.result });
                                        };
                                        reader.readAsDataURL(file);
                                    }}
                                    id="logo-upload"
                                    className="hidden"
                                    data-testid="settings-logo-upload"
                                />
                                <label 
                                    htmlFor="logo-upload"
                                    className={`cursor-pointer inline-flex items-center justify-center w-full sm:w-auto px-4 py-3 sm:py-2 rounded-lg text-sm font-medium transition-colors ${
                                        theme === 'dark'
                                        ? 'bg-slate-700 hover:bg-slate-600 text-slate-200'
                                        : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                                    }`}
                                >
                                    {t('Upload Logo')}
                                </label>
                                <p className={`text-xs mt-2 ${theme === 'dark' ? 'text-slate-500' : 'text-slate-500'}`}>{t('Max 2MB. Square image recommended.')}</p>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end">
                        <button 
                            type="submit" 
                            disabled={loading}
                            data-testid="settings-save-button"
                            className={`w-full sm:w-auto min-h-[44px] px-6 py-3 sm:py-2.5 rounded-lg font-bold shadow-sm transition-all flex justify-center items-center gap-2 ${
                                theme === 'dark' 
                                ? 'bg-amber-500 hover:bg-amber-400 text-slate-950 disabled:opacity-50' 
                                : 'bg-[#0A192F] hover:bg-[#0F284E] text-white disabled:opacity-50'
                            }`}
                        >
                            {loading ? t('Saving...') : t('Save Changes')}
                        </button>
                    </div>
                </form>
            </div>
        </TenantLayout>
    );
}
